
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { Upload, Search, Download, Clock, DollarSign, Shield, Users, FileText, Zap } from "lucide-react";

const Landing = () => {
  const workflowSteps = [
    {
      icon: Upload,
      title: "Upload Documents",
      description: "Securely upload thousands of case documents in various formats"
    },
    {
      icon: Search,
      title: "AI-Powered Search",
      description: "Use natural language prompts to find specific case-related context"
    },
    {
      icon: Download,
      title: "Export Results",
      description: "Export relevant document batches with metadata and references"
    }
  ];

  const testimonials = [
    {
      name: "Sarah Mitchell",
      role: "Senior Partner, Mitchell & Associates",
      content: "ExhibitAI reduced our document review time by 80%. What used to take weeks now takes days.",
      firm: "Mitchell & Associates"
    },
    {
      name: "David Chen",
      role: "Legal Operations Manager",
      content: "The ability to search through 10,000+ documents with natural language queries is game-changing.",
      firm: "Chen Legal Group"
    },
    {
      name: "Jennifer Adams",
      role: "Litigation Attorney",
      content: "Our clerks can now focus on legal analysis instead of manual document sorting. Incredible time savings.",
      firm: "Adams Law Firm"
    }
  ];

  const features = [
    {
      icon: Shield,
      title: "Enterprise Security",
      description: "Bank-grade encryption and compliance with legal industry standards"
    },
    {
      icon: Users,
      title: "Team Collaboration",
      description: "Share findings and organize documents across your legal team"
    },
    {
      icon: FileText,
      title: "Multiple Formats",
      description: "Support for PDFs, Word docs, images, and scanned documents"
    },
    {
      icon: Zap,
      title: "Lightning Fast",
      description: "Get search results in seconds, not hours of manual review"
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gradient-to-r from-primary to-primary-glow rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold">ExhibitAI</span>
          </div>
          <div className="flex items-center space-x-4">
            <Link to="/login">
              <Button variant="ghost">Sign In</Button>
            </Link>
            <Link to="/register">
              <Button>Get Started</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-6 py-20 text-center">
        <Badge variant="secondary" className="mb-6">
          <Clock className="w-4 h-4 mr-2" />
          Save 80% of Document Review Time
        </Badge>
        <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary to-primary-glow bg-clip-text text-transparent">
          AI-Powered Legal<br />Document Search
        </h1>
        <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
          Upload thousands of case documents and find specific context in seconds using natural language AI prompts. 
          Transform weeks of manual review into hours of focused legal work.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
          <Link to="/register">
            <Button size="lg" className="w-full sm:w-auto">
              <DollarSign className="w-5 h-5 mr-2" />
              Start Saving Time & Money
            </Button>
          </Link>
          <Link to="/login">
            <Button variant="outline" size="lg" className="w-full sm:w-auto">
              Watch Demo
            </Button>
          </Link>
        </div>
        <div className="flex justify-center items-center space-x-8 text-sm text-muted-foreground">
          <div className="flex items-center">
            <Shield className="w-4 h-4 mr-2" />
            SOC 2 Compliant
          </div>
          <div className="flex items-center">
            <Users className="w-4 h-4 mr-2" />
            500+ Law Firms
          </div>
          <div className="flex items-center">
            <Clock className="w-4 h-4 mr-2" />
            24/7 Support
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="bg-muted/30 py-20">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-6">See ExhibitAI in Action</h2>
          <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
            Watch how legal professionals are transforming their document review process with AI-powered search.
          </p>
          <div className="max-w-4xl mx-auto">
            <div className="aspect-video bg-card rounded-lg border-2 border-dashed border-border flex items-center justify-center">
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Zap className="w-8 h-8 text-primary" />
                </div>
                <p className="text-muted-foreground">Demo Video Coming Soon</p>
                <p className="text-sm text-muted-foreground mt-2">Contact us for a live demonstration</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Workflow Section */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-6">Simple 3-Step Workflow</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              From document upload to actionable insights in minutes, not days.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {workflowSteps.map((step, index) => (
              <Card key={index} className="text-center p-6">
                <CardContent className="p-0">
                  <div className="w-16 h-16 bg-gradient-to-r from-primary to-primary-glow rounded-full flex items-center justify-center mx-auto mb-4">
                    <step.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{step.title}</h3>
                  <p className="text-muted-foreground">{step.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="bg-muted/30 py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-6">Built for Legal Professionals</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Enterprise-grade features designed specifically for the legal industry's unique requirements.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="p-6 text-center">
                <CardContent className="p-0">
                  <feature.icon className="w-12 h-12 text-primary mx-auto mb-4" />
                  <h3 className="font-semibold mb-2">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-6">Trusted by Leading Law Firms</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              See how legal professionals are transforming their practice with ExhibitAI.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="p-6">
                <CardContent className="p-0">
                  <div className="flex items-center mb-4">
                    <div className="flex text-primary">
                      {[...Array(5)].map((_, i) => (
                        <span key={i}>★</span>
                      ))}
                    </div>
                  </div>
                  <p className="text-muted-foreground mb-4 italic">"{testimonial.content}"</p>
                  <div>
                    <p className="font-semibold">{testimonial.name}</p>
                    <p className="text-sm text-muted-foreground">{testimonial.role}</p>
                    <p className="text-sm text-primary">{testimonial.firm}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-primary to-primary-glow py-20 text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to Transform Your Document Review?</h2>
          <p className="text-xl mb-8 text-white/90 max-w-2xl mx-auto">
            Join hundreds of law firms already saving time and money with AI-powered document search.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button size="lg" variant="secondary" className="w-full sm:w-auto">
                Start Free Trial
              </Button>
            </Link>
            <Button size="lg" variant="outline" className="w-full sm:w-auto border-white text-white hover:bg-white hover:text-primary">
              Schedule Demo
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-card border-t py-12">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-8 h-8 bg-gradient-to-r from-primary to-primary-glow rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold">ExhibitAI</span>
              </div>
              <p className="text-muted-foreground text-sm">
                AI-powered document search for the legal industry. Transform your practice with intelligent automation.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="#" className="hover:text-primary">Features</a></li>
                <li><a href="#" className="hover:text-primary">Pricing</a></li>
                <li><a href="#" className="hover:text-primary">Security</a></li>
                <li><a href="#" className="hover:text-primary">Integrations</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="#" className="hover:text-primary">About</a></li>
                <li><a href="#" className="hover:text-primary">Careers</a></li>
                <li><a href="#" className="hover:text-primary">Blog</a></li>
                <li><a href="#" className="hover:text-primary">Press</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="#" className="hover:text-primary">Contact</a></li>
                <li><a href="#" className="hover:text-primary">Help Center</a></li>
                <li><a href="#" className="hover:text-primary">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-primary">Terms of Service</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t mt-8 pt-8 text-center text-sm text-muted-foreground">
            <p>&copy; 2024 ExhibitAI. All rights reserved. | Contact: hello@exhibitai.com | Phone: (555) 123-4567</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;